import React, { useEffect, useState } from "react";
import ImageTab from "./image-tab.tsx";
import WebcamTab from "./webcam-tab.tsx";

// Update these types to match the actual API response structure
interface DetectionResult {
  bbox: number[];
  confidence: number;
  class_id: number;
  class_name: string;
  plate_text?: string;
  ocr_confidence?: number;
  vehicle_type?: string;
  region?: string;
}

// Updated interface for the new API response structure
interface ProcessResponse {
  filename?: string;
  vehicle_image?: string;
  plate_image?: string;
  processing_time?: number;
  timestamp?: string;
  session_id?: number;

  // New structure fields
  detected_vehicle_image_path?: string;
  detected_plate_image_path?: string;
  list_vehicle_and_plate_information?: Array<{
    vehicle: {
      vehicle_index: number;
      vehicle_image_path: string;
      vehicle_class: string;
      vehicle_bounding_box: number[];
      vehicle_confidence_score: number;
    };
    plate?: {
      plate_index: number;
      plate_image_path: string;
      plate_bounding_box: number[];
      plate_confidence_score: number;
      plate_text?: string;
      ocr_confidence?: number;
    };
  }>;

  // For backward compatibility
  detections?: {
    vehicles: DetectionResult[];
    plates: DetectionResult[];
  };
  detection_ids?: number[];
}

interface DashboardContentProps {
  tabOptions: {
    label: string;
    icon: React.ElementType;
    active: boolean;
  }[];
  activeTab: "image" | /* "video" | */ "webcam";
  selectedImage: string | null;
  isDragging: boolean;
  isActionDisabled: boolean;
  isProcessing: boolean; // Add this line
  processResult?: ProcessResponse | null;
  processError?: string | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  updateImageStates: (dataUrl: string) => void;
  handleTabClick: (tab: "image" | /* "video" | */ "webcam") => void;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleReset: () => void;
  handleProcessDetection: () => void;
  captureFromWebcam: (showGreenbox: boolean) => void;
  setIsDragging: (dragging: boolean) => void;
  cameraDevices: MediaDeviceInfo[];
  selectedCameraId: string | undefined;
  setSelectedCameraId: (id: string) => void;
}

interface Location {
  id: number;
  name: string;
  type: "Entry" | "Exit";
  timestamp: string;
}

const LOCATION_OPTIONS = [
  "Gate 1 - Entrance",
  "Gate 2 - Entrance",
  "Gate 3 - Exit",
  "Gate 4 - Exit",
];

const DashboardContent: React.FC<DashboardContentProps> = ({
  tabOptions,
  activeTab,
  selectedImage,
  isDragging,
  isActionDisabled,
  isProcessing: isProcessingProp,
  processResult,
  processError,
  videoRef,
  canvasRef,
  updateImageStates,
  handleTabClick,
  handleFileUpload,
  handleReset,
  handleProcessDetection,
  captureFromWebcam,
  setIsDragging,
  cameraDevices,
  selectedCameraId,
  setSelectedCameraId,
}) => {
  // Local state for processing
  const [isProcessing, setIsProcessing] = useState<boolean>(isProcessingProp);
  // Local state for process result (renamed to avoid duplicate identifier)
  const [localProcessResult, setLocalProcessResult] = useState<ProcessResponse | null>(null);

  // Add state for location data
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [selectedLocation, setSelectedLocation] = React.useState<string>("");

  // Notification state
  const [notification, setNotification] = useState<{
    type: "success" | "error" | "warning" | "info";
    message: string;
    visible: boolean;
  }>({
    type: "info",
    message: "",
    visible: false
  });

  // Result modal state
  const [showResultModal, setShowResultModal] = useState(false);

  // Fetch locations on component mount
  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await fetch("https://galaxy-alpr-be.onrender.com/api/locations");
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      setLocations(data);

      // Select first location by default if none selected
      if (data.length > 0 && !selectedLocation) {
        setSelectedLocation(data[0].name);
      }
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Helper functions for image processing
  const extractFilename = (path: string | undefined): string | null => {
    if (!path) return null;
    const parts = path.split(/[\\/]/); // Split by both forward and backward slashes
    return parts[parts.length - 1]; // Return the last part (filename)
  };

  const getImageUrl = (path: string | undefined, type: 'vehicles' | 'plates'): string => {
    if (!path) return '';

    // If the path is already a URL, return it
    if (path.startsWith('http')) return path;

    // Extract filename
    const filename = extractFilename(path);
    if (!filename) return '';

    // Check if path contains full directory structure or just filename
    if (path.includes('/') || path.includes('\\')) {
      // For paths with directory structure, handle both crops and regular images
      if (path.includes('crops')) {
        return `https://galaxy-alpr-be.onrender.com/outputs/${type}/crops/${filename}`;
      } else {
        return `https://galaxy-alpr-be.onrender.com/outputs/${type}/${filename}`;
      }
    } else {
      // For simple filenames, assume they're in the main directory
      return `https://galaxy-alpr-be.onrender.com/outputs/${type}/${filename}`;
    }
  };

  const formatConfidence = (confidence: number | undefined): string => {
    if (confidence === undefined || confidence === null) return '0.0%';

    // Check if confidence is already in percentage format (0-100)
    if (confidence > 1) {
      return `${confidence.toFixed(1)}%`;
    }

    // Otherwise, convert from decimal (0-1) to percentage
    return `${(confidence * 100).toFixed(1)}%`;
  };

  // Updated process detection function with improved UX
  const handleLocalProcessDetection = async () => {
    if (!selectedImage) return;

    // Show processing notification
    setNotification({
      type: "info",
      message: "Processing your image... Please wait.",
      visible: true
    });

    setIsProcessing(true);
    try {
      // Convert data URL to Blob
      const response = await fetch(selectedImage);
      const blob = await response.blob();

      // Create FormData and append the image file
      const formData = new FormData();
      formData.append('file', blob, 'image.jpg');

      // Add location if selected
      if (selectedLocation) {
        formData.append('location', selectedLocation);
      }

      // Call the API with the FormData
      const processResponse = await fetch('https://galaxy-alpr-be.onrender.com/detect/image', {
        method: 'POST',
        body: formData,
      });

      // Handle the response
      if (!processResponse.ok) {
        const errorData = await processResponse.json();
        throw new Error(errorData.detail || 'Failed to process image');
      }

      const result = await processResponse.json();
      console.log("Detection result:", result);

      // Check if we have valid detections
      const hasValidVehicles =
        result?.list_vehicle_and_plate_information?.length > 0 ||
        result?.detections?.vehicles?.length > 0;

      const hasValidPlates =
        result?.list_vehicle_and_plate_information?.some(item => item?.plate) ||
        result?.detections?.plates?.length > 0;

      // Set process result
      setLocalProcessResult(result);

      // Clear notification
      setNotification({ type: "info", message: "", visible: false });

      if (hasValidPlates) {
        // Has valid license plate detections - show success and offer to view details
        setShowResultModal(true);
      } else if (hasValidVehicles) {
        // Has vehicles but no plates - show partial success
        setNotification({
          type: "warning",
          message: "Vehicles detected, but no license plates could be identified.",
          visible: true
        });
        // Show result modal with limited success
        setShowResultModal(true);
      } else {
        // No detections at all - show appropriate message
        setNotification({
          type: "warning",
          message: "No vehicles or license plates were detected in this image. Please try a different image or adjust the detection area.",
          visible: true
        });
      }
    } catch (error) {
      console.error('Detection processing error:', error);
      // Show error notification
      setNotification({
        type: "error",
        message: error instanceof Error ? error.message : "An unknown error occurred during processing.",
        visible: true
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Auto-hide notifications after 6 seconds
  useEffect(() => {
    let timeoutId: number | undefined;
    if (notification.visible) {
      timeoutId = window.setTimeout(() => {
        setNotification(prev => ({ ...prev, visible: false }));
      }, 6000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [notification.visible]);

  return (
    <div className="p-8 relative">
      {/* Notification Toast */}
      {notification.visible && (
        <div className="fixed top-4 right-4 z-50 max-w-md animate-fadeIn">
          <div className={`p-4 rounded-lg shadow-lg flex items-start ${notification.type === 'success' ? 'bg-green-50 border-l-4 border-green-500 text-green-700' :
            notification.type === 'error' ? 'bg-red-50 border-l-4 border-red-500 text-red-700' :
              notification.type === 'warning' ? 'bg-yellow-50 border-l-4 border-yellow-500 text-yellow-700' :
                'bg-blue-50 border-l-4 border-blue-500 text-blue-700'
            }`}>
            <div className="flex-shrink-0 mr-3">
              {notification.type === 'success' && (
                <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
              {notification.type === 'error' && (
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              {notification.type === 'warning' && (
                <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              {notification.type === 'info' && (
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div>
              <p className="text-sm">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification(prev => ({ ...prev, visible: false }))}
              className="ml-auto -mx-1.5 -my-1.5 bg-transparent text-gray-400 hover:text-gray-900 rounded-lg p-1.5 inline-flex h-8 w-8"
            >
              <span className="sr-only">Close</span>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Detection Results Modal */}
      {showResultModal && localProcessResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Detection Results</h2>
              <button
                onClick={() => setShowResultModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Processing Time and Detection Count */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {localProcessResult.processing_time?.toFixed(2) || "0.00"}s
                </div>
                <div className="text-sm text-gray-600">Processing Time</div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {localProcessResult.list_vehicle_and_plate_information?.length ||
                    localProcessResult.detections?.vehicles?.length || 0}
                </div>
                <div className="text-sm text-gray-600">Detections</div>
              </div>
            </div>

            {/* License Plate Detections */}
            <h3 className="text-lg font-medium text-gray-800 mb-2">
              License Plate Detections
            </h3>
            {localProcessResult.list_vehicle_and_plate_information?.some(item => item.plate) ? (
              localProcessResult.list_vehicle_and_plate_information
                .filter(item => item.plate)
                .map((item, index) => (
                  <div
                    key={`plate-${index}`}
                    className="mb-2 p-3 bg-gray-50 rounded"
                  >
                    <p>
                      <span className="font-semibold">License Plate:</span>{" "}
                      {item.plate?.plate_text || "unknown"}
                      <span className="ml-2 font-semibold">
                        Confidence:
                      </span>{" "}
                      {formatConfidence(item.plate?.plate_confidence_score)}
                      {item.vehicle?.vehicle_class && (
                        <>
                          <span className="ml-2 font-semibold">Vehicle Type:</span>{" "}
                          {item.vehicle.vehicle_class || "unknown"}
                        </>
                      )}
                    </p>
                  </div>
                ))
            ) : (
              <p className="text-gray-500 mb-4">
                No license plates detected
              </p>
            )}

            {/* Vehicle Detections */}
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
              Vehicle Detections
            </h3>
            {localProcessResult.list_vehicle_and_plate_information?.length ? (
              localProcessResult.list_vehicle_and_plate_information.map((item, index) => (
                <div
                  key={`vehicle-${index}`}
                  className="mb-2 p-3 bg-blue-50 rounded"
                >
                  <p>
                    <span className="font-semibold">Vehicle Type:</span>{" "}
                    {item.vehicle.vehicle_class || "unknown"}
                    <span className="ml-2 font-semibold">
                      Confidence:
                    </span>{" "}
                    {formatConfidence(item.vehicle.vehicle_confidence_score)}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-gray-500 mb-4">No vehicles detected</p>
            )}

            {/* Detection Images */}
            <div className="mt-4 border-t pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Vehicle Image */}
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-3">
                    Vehicle Detection
                  </h3>
                  {localProcessResult.list_vehicle_and_plate_information?.[0]?.vehicle?.vehicle_image_path ? (
                    <img
                      src={getImageUrl(localProcessResult.list_vehicle_and_plate_information[0].vehicle.vehicle_image_path, 'vehicles')}
                      alt="Vehicle detection"
                      className="rounded-lg max-h-64 shadow-md w-full object-cover"
                    />
                  ) : localProcessResult.detected_vehicle_image_path ? (
                    <img
                      src={getImageUrl(localProcessResult.detected_vehicle_image_path, 'vehicles')}
                      alt="Vehicle detection"
                      className="rounded-lg max-h-64 shadow-md w-full object-cover"
                    />
                  ) : (
                    <div className="bg-gray-100 h-64 rounded-lg flex items-center justify-center">
                      <p className="text-gray-500">No vehicle image available</p>
                    </div>
                  )}
                </div>

                {/* Plate Image */}
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-3">
                    Plate Detection
                  </h3>
                  {localProcessResult.list_vehicle_and_plate_information?.some(item => item.plate) ? (
                    <img
                      src={getImageUrl(
                        localProcessResult.list_vehicle_and_plate_information.find(item => item.plate)?.plate?.plate_image_path,
                        'plates'
                      )}
                      alt="Plate detection"
                      className="rounded-lg max-h-64 shadow-md w-full object-cover"
                    />
                  ) : localProcessResult.detected_plate_image_path ? (
                    <img
                      src={getImageUrl(localProcessResult.detected_plate_image_path, 'plates')}
                      alt="Plate detection"
                      className="rounded-lg max-h-64 shadow-md w-full object-cover"
                    />
                  ) : (
                    <div className="bg-gray-100 h-64 rounded-lg flex items-center justify-center">
                      <p className="text-gray-500">No plate image available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-center space-x-3">
              <button
                onClick={() => setShowResultModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Continue Working
              </button>

              <a
                href="/history"
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors inline-block"
              >
                View All Detections
              </a>

              <a
                href="/result"
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors inline-block"
              >
                Go to Detailed Results
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Tab Selection */}
      <div className="flex justify-center mb-8">
        <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-full p-2 shadow-lg">
          <div className="flex gap-2">
            {tabOptions.map((tab, index) => (
              <button
                key={index}
                onClick={() =>
                  handleTabClick(tab.label.toLowerCase() as "image" | "webcam")
                }
                className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${tab.active
                  ? "bg-white text-gray-800 shadow-md font-medium"
                  : "text-gray-600 hover:text-gray-800"
                  }`}
              >
                <tab.icon size={20} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-2xl mx-auto">
        <div className="bg-white bg-opacity-100 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
          {/* Tab Content */}
          {activeTab === "webcam" ? (
            <WebcamTab
              videoRef={videoRef}
              canvasRef={canvasRef}
              selectedCameraId={selectedCameraId || ""}
              cameraDevices={cameraDevices}
              setSelectedCameraId={setSelectedCameraId}
              selectedImage={selectedImage}
              updateImageStates={updateImageStates}
              handleReset={handleReset}
              selectedLocation={selectedLocation}
              loadingLocations={loadingLocations}
              locations={locations}
              setSelectedLocation={setSelectedLocation}
            />
          ) : (
            <ImageTab
              selectedImage={selectedImage}
              handleFileUpload={handleFileUpload}
              setIsDragging={setIsDragging}
              isDragging={isDragging}
              selectedLocation={selectedLocation}
              loadingLocations={loadingLocations}
              locations={locations}
              setSelectedLocation={setSelectedLocation}
              handleReset={handleReset}
              isActionDisabled={isActionDisabled}
            />
          )}

          {/* Process Detection Button - Animation Enhancement */}
          <div className="mt-8 flex justify-center">
            <button
              onClick={handleLocalProcessDetection}
              disabled={isActionDisabled || isProcessing}
              className={`px-6 py-3 rounded-lg transition-all ${isActionDisabled || isProcessing
                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600 shadow hover:shadow-md transform hover:-translate-y-0.5"
                }`}
            >
              {isProcessing ? (
                <span className="flex items-center">
                  <span className="inline-block w-4 h-4 mr-2 border-2 border-t-transparent border-white rounded-full animate-spin"></span>
                  Processing...
                </span>
              ) : (
                "Process Detection"
              )}
            </button>
          </div>

          {/* Error message display */}
          {processError && (
            <div className="mt-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">Error: {processError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Remove direct rendering of results - now shown in modal instead */}
        </div>
      </div>
    </div>
  );
};

export default DashboardContent;
